// Page « Offre et couverture » (spec § 3) : l'offre de l'utilisateur et un estimateur du nombre de fiches que la
// base recense par zone et par pays, pour qu'un partenaire choisisse ses territoires en connaissance de cause. Les
// comptes viennent de `coverage_estimate` (toute la base, des nombres seulement).
import { ChevronRight, Monitor, X } from "lucide-react";
import {
  CanAccess,
  useCanAccess,
  useGetIdentity,
  useGetList,
  useGetOne,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Link, Navigate } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemGroup,
  ItemTitle,
} from "@/components/ui/item";

import { ReplayIntroLink } from "../onboarding";
import type { Sale, UserTerritory } from "../types";
import { Pricing } from "./Pricing";
import { useInvited } from "./useInvited";
import { countryName } from "./projectUtils";
import {
  type Coverage,
  coverageOf,
  territoryLabel,
  useCoverage,
  useZones,
} from "./territories";

export const OfferPage = () => {
  const translate = useTranslate();
  const invited = useInvited();
  // Page cachée à tous sauf au gestionnaire tant que les prix se négocient (règle `offer` de canAccess) : une adresse
  // tapée mène à l'accueil.
  const { canAccess, isPending } = useCanAccess({
    resource: "offer",
    action: "read",
  });
  if (isPending) return null;
  if (!canAccess) return <Navigate to="/" replace />;
  return (
    <div className="mt-2 pb-4 flex flex-col gap-6 max-w-5xl">
      <div>
        <h2 className="text-xl font-semibold">
          {translate("resources.projects.offer.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("resources.projects.offer.intro")}
        </p>
      </div>
      <CurrentOffer />
      <Pricing />
      <CoverageEstimator />
      {invited && (
        <p className="text-sm text-muted-foreground">
          <ReplayIntroLink />
        </p>
      )}
    </div>
  );
};

OfferPage.path = "/offer";

/** Vrai si la page est ouverte à ce compte : sinon, aucun lien n'y mène. */
export const useOfferOpen = () =>
  useCanAccess({ resource: "offer", action: "read" }).canAccess === true;

/** Lien vers la page, dans les paramètres sur téléphone. */
export const OfferSection = () => {
  const translate = useTranslate();
  return (
    <ItemGroup className="rounded-lg border overflow-hidden">
      <Item asChild size="sm" className="cursor-pointer">
        <Link to={OfferPage.path}>
          <ItemContent>
            <ItemTitle className="font-normal">
              {translate("resources.projects.offer.title")}
            </ItemTitle>
          </ItemContent>
          <ItemActions>
            <ChevronRight className="size-4 text-muted-foreground" />
          </ItemActions>
        </Link>
      </Item>
    </ItemGroup>
  );
};

/** Sur téléphone, pour un administrateur : les écrans de gestion restent sur ordinateur. */
export const DesktopOnlySection = () => {
  const translate = useTranslate();
  return (
    <CanAccess resource="sales" action="list">
      <p className="flex items-start gap-2 rounded-lg border px-3 py-2 text-sm text-muted-foreground">
        <Monitor className="mt-0.5 size-4 shrink-0" />
        {translate("resources.projects.offer.desktop_only")}
      </p>
    </CanAccess>
  );
};

const CurrentOffer = () => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { identity } = useGetIdentity();
  const { data: sale } = useGetOne<Sale>(
    "sales",
    { id: identity?.id ?? 0 },
    { enabled: !!identity?.id },
  );
  const { data: territories } = useGetList<UserTerritory>(
    "user_territories",
    {
      filter: { sales_id: identity?.id },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: !!identity?.id && !!sale?.partner },
  );
  if (!sale) return null;

  const kind = !sale.partner
    ? "team"
    : sale.partner_offer === "subscriber"
      ? "subscriber"
      : "invited";
  return (
    <Card>
      <CardContent className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-bold">
            {translate("resources.projects.offer.yours")}
          </h3>
          <Badge variant={kind === "invited" ? "outline" : "secondary"}>
            {translate(`resources.projects.offer.${kind}`)}
          </Badge>
        </div>
        <p className="text-sm">
          {translate(`resources.projects.offer.${kind}_help`)}
        </p>
        {kind === "subscriber" && (
          <div className="flex flex-wrap gap-2">
            {sale.partner_world ? (
              <Badge variant="outline">
                {translate("resources.projects.offer.world")}
              </Badge>
            ) : (
              territories?.map((territory) => (
                <Badge key={territory.id} variant="outline">
                  {territoryLabel(territory, locale, translate)}
                </Badge>
              ))
            )}
          </div>
        )}
        {kind !== "team" && (
          <p className="text-sm text-muted-foreground">
            {translate("resources.projects.offer.change")}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const CoverageEstimator = () => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { data: rows } = useCoverage();
  const { data: zones } = useZones();
  const [world, setWorld] = useState(false);
  const [chosenZones, setChosenZones] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [country, setCountry] = useState("");
  if (!rows || !zones) return null;

  const code = country.trim().toUpperCase();
  const known = rows.some((row) => row.country === code);
  const selection = world
    ? coverageOf(rows, zones, "world")
    : coverageOf(rows, zones, { zones: chosenZones, countries });
  const empty = !world && !chosenZones.length && !countries.length;
  // Pays recensés hors de toute zone, les plus fournis d'abord.
  const zoned = new Set(zones.flatMap((zone) => zone.countries));
  const outside = rows
    .filter((row) => !zoned.has(row.country))
    .sort((a, b) => Number(b.projects_30d) - Number(a.projects_30d));

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div>
          <h3 className="text-sm font-bold">
            {translate("resources.projects.offer.estimator")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {translate("resources.projects.offer.estimator_help")}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2 font-normal" />
                <th className="py-1 pr-2 font-normal text-right">
                  {translate("resources.projects.offer.countries")}
                </th>
                <th className="py-1 pr-2 font-normal text-right">
                  {translate("resources.projects.offer.projects_30d")}
                </th>
                <th className="py-1 pr-2 font-normal text-right">
                  {translate("resources.projects.offer.high_score_30d")}
                </th>
                <th className="py-1 font-normal text-right">
                  {translate("resources.projects.offer.projects")}
                </th>
              </tr>
            </thead>
            <tbody>
              <CoverageLine
                label={translate("resources.projects.offer.world")}
                coverage={coverageOf(rows, zones, "world")}
                checked={world}
                onCheckedChange={setWorld}
                strong
              />
              {zones.map((zone) => (
                <CoverageLine
                  key={zone.code}
                  label={translate(`resources.projects.zones.${zone.code}`)}
                  detail={zone.countries
                    .map((item) => countryName(item, locale))
                    .join(", ")}
                  coverage={coverageOf(rows, zones, {
                    zones: [zone.code],
                    countries: [],
                  })}
                  checked={world || chosenZones.includes(zone.code)}
                  disabled={world}
                  onCheckedChange={(value) =>
                    setChosenZones((current) =>
                      value
                        ? [...current, zone.code]
                        : current.filter((item) => item !== zone.code),
                    )
                  }
                />
              ))}
              {outside.length > 0 && (
                <CoverageLine
                  label={translate("resources.projects.offer.outside")}
                  detail={outside
                    .slice(0, 12)
                    .map(
                      (row) =>
                        `${countryName(row.country, locale)} ${row.projects_30d}`,
                    )
                    .join(", ")}
                  coverage={coverageOf(rows, zones, {
                    zones: [],
                    countries: outside.map((row) => row.country),
                  })}
                />
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-xs text-muted-foreground tracking-wide">
            {translate("resources.projects.offer.add_country")}
          </span>
          <form
            className="flex flex-wrap gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (known && !countries.includes(code)) {
                setCountries([...countries, code]);
                setCountry("");
              }
            }}
          >
            <Input
              className="w-28"
              list="coverage-countries"
              placeholder={translate("resources.projects.partners.country")}
              value={country}
              onChange={(event) => setCountry(event.target.value)}
              maxLength={2}
              disabled={world}
            />
            <datalist id="coverage-countries">
              {rows.map((row) => (
                <option key={row.country} value={row.country}>
                  {countryName(row.country, locale)}
                </option>
              ))}
            </datalist>
            <Button
              type="submit"
              size="sm"
              variant="outline"
              disabled={world || !known}
            >
              {translate("resources.projects.partners.add_territory")}
            </Button>
          </form>
          {!!countries.length && (
            <div className="flex flex-wrap gap-2">
              {countries.map((item) => (
                <Badge key={item} variant="outline" className="gap-1">
                  {countryName(item, locale)}
                  <button
                    type="button"
                    aria-label={translate("ra.action.delete")}
                    onClick={() =>
                      setCountries(countries.filter((other) => other !== item))
                    }
                  >
                    <X className="size-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>
        <p className="text-sm font-medium">
          {empty
            ? translate("resources.projects.offer.select")
            : translate("resources.projects.offer.summary", {
                smart_count: selection.projects_30d,
                countries: selection.countries,
                high: selection.high_score_30d,
                total: selection.projects,
              })}
        </p>
        <p className="text-xs text-muted-foreground">
          {translate("resources.projects.offer.estimate_note")}
        </p>
      </CardContent>
    </Card>
  );
};

const CoverageLine = ({
  label,
  detail,
  coverage,
  checked,
  disabled,
  onCheckedChange,
  strong,
}: {
  label: string;
  detail?: string;
  coverage: Coverage;
  checked?: boolean;
  disabled?: boolean;
  onCheckedChange?: (value: boolean) => void;
  strong?: boolean;
}) => (
  <tr className="border-t align-top">
    <td className="py-2 pr-2">
      <label className="flex gap-2 items-start">
        {onCheckedChange ? (
          <Checkbox
            className="mt-0.5"
            checked={!!checked}
            disabled={disabled}
            onCheckedChange={(value) => onCheckedChange(value === true)}
          />
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <span className="flex flex-col">
          <span className={strong ? "font-medium" : undefined}>{label}</span>
          {detail && (
            <span className="text-xs text-muted-foreground">{detail}</span>
          )}
        </span>
      </label>
    </td>
    <td className="py-2 pr-2 text-right tabular-nums">{coverage.countries}</td>
    <td className="py-2 pr-2 text-right tabular-nums font-medium">
      {coverage.projects_30d}
    </td>
    <td className="py-2 pr-2 text-right tabular-nums">
      {coverage.high_score_30d}
    </td>
    <td className="py-2 text-right tabular-nums text-muted-foreground">
      {coverage.projects}
    </td>
  </tr>
);
